import { Injectable, signal } from '@angular/core';
import type { CmdkItem, CmdkNewsType } from '../models/cmdk.model';

/**
 * RESPONSIBILITY: what the viewer opened from the palette last, so they do not
 * retype the same room name every morning.
 *
 * SIGNALS:
 *   records — most-recent-first, capped per kind
 *
 * USED BY: `CmdkRecentsProvider` (reads), `CmdkComponent.activate()` (writes).
 *
 * BACKEND CONTRACT: none, deliberately. `myRecentClicks(limit)` exists in the
 * schema and would give cross-device recents, but its read side has no callers
 * and `ConnectionsService.trackClick`'s `clickType` union has no `'command'`
 * member — sending one would put an unrecognised type into the click analytics
 * that feed the apps rail. localStorage now; the server store is a v2 that
 * needs a backend confirmation first (SPEC-command-k.md §3.7).
 *
 * AI CONTEXT — two rules that decide what this can honestly store
 *
 * **Only the kinds that were expensive to find.** Spaces, people, apps and
 * news get remembered; navigation and actions do not. The latter are already
 * in "Suggested actions" on an empty palette, one keystroke away, and their
 * `execute` is a closure the registry owns — a stored copy would be a stale
 * function that cannot be serialised anyway. Recents remember what you
 * SEARCHED for, not what you can already see.
 *
 * **Nothing volatile is stored.** A room's availability, a colleague's desk —
 * these are true for minutes. A recent row rebuilt from a week-old snapshot
 * that still says "Available" is worse than no row at all, so the reconstructed
 * items set every volatile field to `unknown`, which the rows already render as
 * "say nothing". The stored fields are the ones needed to OPEN the thing again,
 * plus a label to recognise it by.
 */

/** The serialisable shadow of a `CmdkItem`. */
export interface CmdkRecentRecord {
  readonly kind: 'space' | 'person' | 'app' | 'news';
  readonly id: string;
  readonly title: string;
  readonly subtitle?: string;
  /** Epoch ms. Ordering only — never rendered. */
  readonly at: number;
  /** space: the detail panel takes it as a hint. */
  readonly floorName?: string;
  /** news: decides the glyph AND which of two routes opens it. */
  readonly newsType?: CmdkNewsType;
  /** news (knowledge pages only): the page route needs both ids. */
  readonly workspaceId?: string;
  /** app: `CmdkAppItem.url` is required, and the launcher validates it. */
  readonly url?: string;
}

const STORAGE_KEY = 'im-cmdk-recents';
/** Per kind, not overall — otherwise one busy kind evicts all the others. */
const CAP_PER_KIND = 8;

type RecentKind = CmdkRecentRecord['kind'];

const REMEMBERED: ReadonlySet<string> = new Set<RecentKind>(['space', 'person', 'app', 'news']);

@Injectable({ providedIn: 'root' })
export class CmdkRecentsService {
  private readonly _records = signal<readonly CmdkRecentRecord[]>(this.load());
  readonly records = this._records.asReadonly();

  /**
   * Record an activation. Silently ignores kinds that are not remembered, so
   * the caller does not need to know the rule.
   *
   * Most-recent-first, de-duplicated on `kind:id` so re-opening the same room
   * moves it up rather than filling the list with copies.
   */
  record(item: CmdkItem): void {
    if (!REMEMBERED.has(item.kind)) return;
    const entry = toRecord(item);
    if (!entry) return;

    // `update` rather than read-then-write: the read would enrol `_records` as
    // a dependency of any effect that calls this, and the write would dirty it
    // — the 43fa13ba shape. `update` takes the current value without tracking.
    this._records.update(previous => {
      const withoutDuplicate = previous.filter(r => !(r.kind === entry.kind && r.id === entry.id));
      const next = [entry, ...withoutDuplicate];
      const kept: CmdkRecentRecord[] = [];
      const perKind = new Map<string, number>();
      for (const record of next) {
        const count = perKind.get(record.kind) ?? 0;
        if (count >= CAP_PER_KIND) continue;
        perKind.set(record.kind, count + 1);
        kept.push(record);
      }
      this.save(kept);
      return kept;
    });
  }

  /** Forget everything. Exposed for a future "clear recents" affordance. */
  clear(): void {
    this._records.set([]);
    this.save([]);
  }

  private load(): readonly CmdkRecentRecord[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      // Validated on the way in, not trusted: this is user-writable storage and
      // a malformed entry would otherwise render a blank row or crash a
      // preview that assumed a field was there.
      return parsed.filter(isRecord);
    } catch {
      // Private browsing, a quota error, or corrupt JSON. Recents are a
      // convenience — losing them must never stop the palette opening.
      return [];
    }
  }

  private save(records: readonly CmdkRecentRecord[]): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
    } catch {
      // Quota or a disabled store. Nothing to tell the user: the palette
      // works, it just will not remember.
    }
  }
}

function toRecord(item: CmdkItem): CmdkRecentRecord | null {
  const base = {
    id: item.id,
    title: item.title,
    subtitle: item.subtitle,
    at: Date.now(),
  };
  switch (item.kind) {
    case 'space':
      return { ...base, kind: 'space', floorName: item.floorName };
    case 'person':
      return { ...base, kind: 'person' };
    case 'app':
      return { ...base, kind: 'app', url: item.url };
    case 'news':
      return { ...base, kind: 'news', newsType: item.newsType, workspaceId: item.workspaceId };
    default:
      return null;
  }
}

function isRecord(value: unknown): value is CmdkRecentRecord {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Partial<CmdkRecentRecord>;
  return (
    typeof v.id === 'string'
    && typeof v.title === 'string'
    && typeof v.at === 'number'
    && typeof v.kind === 'string'
    && REMEMBERED.has(v.kind)
  );
}
